import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useSearchParams} from 'react-router';
import {useShallow} from 'zustand/react/shallow';
import ArrowNextIcon from '@/assets/images/icon-arrow-next.svg?react';
import Button from '@/components/button';
import ContainerCard from '@/components/container-card';
import {useDocumentContext} from '@/components/document/context';
import {useDocumentStore} from '@/components/document/store';
import SectionsSelector from '@/components/sections-selector';
import Title from '@/components/title';
import {BUTTON_ICON_POSITION, BUTTON_THEME} from '@/constants/button';
import {SUMMARY_SECTION} from '@/constants/sections';
import {DEBUG_SECTIONS_SELECTOR_KEY} from '@/constants/storage';
import {DocumentSectionExcerpt} from '@/engine';
import environment from '@/environment';
import {getAnalyticsDocumentSectionVirtualClickUrl} from '@/services/analytics/get-analytics-document-section-virtual-click-url';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {DevInspectorToggle} from '../inspector/dev-inspector-toggle';
import {SavingIndicator} from './saving-indicator';

type Props = {
  isSectionSelectorVisible?: boolean;
  isNextVisible?: boolean;
  showSubmitLabel: boolean;
  isSaving?: boolean;
  hasSavingError?: boolean;
  onNext: () => void;
};

const DocumentToolbar = ({
  isSectionSelectorVisible,
  isNextVisible,
  onNext,
  showSubmitLabel,
  isSaving,
  hasSavingError,
}: Props) => {
  const {t} = useTranslation();
  const location = useLocation();

  const {
    visibleSections,
    currentSectionId,
    currentSectionIndex: activeSectionIndex,
    nextSection,
    document,
    hasUnsavedChanges,
    isSubmitting,
    valueResolver,
  } = useDocumentContext();
  const {documentName, documentType, validity} = useDocumentStore(
    useShallow(state => ({
      documentName: valueResolver(state.meta?.name),
      documentType: valueResolver(state.meta?.type),
      validity: state.validity,
    })),
  );

  const [searchParams] = useSearchParams();

  const isInDebug = Boolean(searchParams.get('debug')) || Boolean(localStorage.getItem(DEBUG_SECTIONS_SELECTOR_KEY));

  const sectionExcerpts: DocumentSectionExcerpt[] = useMemo(() => {
    const isInDevelopment = environment.DEBUG;
    const isForceRequired = isInDebug === true && isInDevelopment;

    return visibleSections.map(section => ({
      id: section.id,
      trackingId: section.trackingId,
      title: section.title,
      isCompleted: validity[section.id]?.valid,
      isRequired: isForceRequired ? true : section.visible,
    }));
  }, [isInDebug, visibleSections, validity]);

  // The farthest section that can be filled
  const currentSectionIndex = Math.max(
    sectionExcerpts.findIndex(section => !section.isCompleted && section.isRequired),
    activeSectionIndex,
    isInDebug ? sectionExcerpts.length - 1 : -1,
  );

  const isSubmitDisabled = useMemo(() => {
    return isSubmitting || !validity[currentSectionId]?.valid;
  }, [isSubmitting, validity, currentSectionId]);

  const isSectionsSelectorDisabled = isSubmitting;

  const isPreviewingPdfs = useMemo(() => {
    return currentSectionId === SUMMARY_SECTION && !validity[currentSectionId]?.valid;
  }, [currentSectionId, validity]);

  return (
    <ContainerCard.Header currentStep={activeSectionIndex} totalSteps={sectionExcerpts.length}>
      <div className="grid grid-cols-3 items-center">
        {isSectionSelectorVisible ? (
          <SectionsSelector
            activeSectionIndex={activeSectionIndex}
            currentSectionIndex={currentSectionIndex}
            isDisabled={isSectionsSelectorDisabled}
            sections={sectionExcerpts}
          />
        ) : (
          <div className="relative flex min-h-10" />
        )}

        <div className="flex flex-col items-center gap-2 text-center">
          <Title data-testid="document-title">{documentName}</Title>
          <div className="text-xs text-violet-400">{documentType}</div>
        </div>

        <div className="flex flex-row items-center justify-end gap-8">
          <SavingIndicator
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            lastSaved={document.updatedAt}
            hasFailed={!!hasSavingError}
          />

          {environment.DEBUG && <DevInspectorToggle data-testid="document-debug-panel" />}

          {isNextVisible && (
            <Button
              allowClickOnDisabled={!isSubmitting && !isPreviewingPdfs}
              data-testid="document-submit"
              // eslint-disable-next-line jsx-a11y/tabindex-no-positive
              tabIndex={999999} // We want the "Next" button to be the last element focused to allow navigation by keyboard between sections
              disabled={isSubmitDisabled}
              icon={<ArrowNextIcon className="h-3" />}
              iconPosition={BUTTON_ICON_POSITION.RIGHT}
              isLoading={isSubmitting}
              onClick={onNext}
              theme={BUTTON_THEME.PRIMARY}
              label={showSubmitLabel ? t('components.document.toolbar.submit') : t('components.document.toolbar.next')}
              trackingKey={
                showSubmitLabel ? WEB_CLICK_TRACKING_KEY.SUBMIT_DOCUMENT : WEB_CLICK_TRACKING_KEY.NEXT_DOCUMENT_STEP
              }
              trackingClickUrl={
                !showSubmitLabel && nextSection
                  ? getAnalyticsDocumentSectionVirtualClickUrl(location, nextSection)
                  : null
              }
            />
          )}
        </div>
      </div>
    </ContainerCard.Header>
  );
};

export default DocumentToolbar;
